import { faBrush, faPalette, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faChevronLeft, faPlus, faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import VoxelSelectPalette from '../../components/muiLegacy/Select/VoxelSelectPalette';
import SlideThemeSelect from '../../components/muiLegacy/TextFieldSelect/SlideThemeSelect';
import VoxelButton from '../../components/voxel/Buttons/VoxelButton';
import VoxelIconButton from '../../components/voxel/Buttons/VoxelIconButton';
import VoxelInput from '../../components/voxel/Forms/VoxelInput';
import { VoxelPopover } from '../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../components/voxel/Popover/VoxelPopoverTrigger';
import { AnalysisColorPaletteGq } from '../../graphql/projects/models/AnalysisColorPaletteGq';
import { ProjectGq } from '../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../graphql/projects/projectsApiService';
import { usersApiService } from '../../graphql/users/usersApiService';
import { AnalysisFilterState } from './Filter/analysisFilterState';
import PalettePage from './Palette/PalettePage';

export interface AnalysisStyleButtonProps {
  project: ProjectGq;
  reload: () => Promise<unknown>;
  palettes: AnalysisColorPaletteGq[];
  analysisFilterState: AnalysisFilterState;
  setAnalysisFilterState: Dispatch<SetStateAction<AnalysisFilterState>>;
}

export const AnalysisStyleButton: React.FC<AnalysisStyleButtonProps> = ({
  project,
  reload,
  palettes,
  analysisFilterState,
  setAnalysisFilterState,
}) => {
  // Dialog logic
  const [page, setPage] = useState<'editPalette' | 'createPalette' | 'start'>('start');

  const { data: paletteList, query } =
    projectsApiService.queries.analysis.useGetAnalysisColorPalettes();

  // Selected palette
  const [selectedPalette, setSelectedPalette] = useState<AnalysisColorPaletteGq | null>(
    paletteList?.find((p) => p.id === project.analysisColorPaletteId)
  );
  useEffect(() => {
    if (project.analysisColorPaletteId !== selectedPalette?.id)
      setSelectedPalette(paletteList?.find((p) => p.id === project.analysisColorPaletteId));
  }, [project.analysisColorPaletteId]);

  // Logic for updating selected palette in backend
  const updateProjectPaletteMutation =
    projectsApiService.mutations.projects.useUpdateProjectAnalysisPalette();

  useEffect(() => {
    if (project.analysisColorPaletteId !== selectedPalette?.id) {
      updateProjectPaletteMutation({
        projectId: project.id,
        paletteId: selectedPalette?.id > 0 ? selectedPalette?.id : null,
      }).then(reload);
    }
  }, [selectedPalette]);

  // Create color palette
  const createPaletteMutation =
    projectsApiService.mutations.projects.useCreateAnalysisColorPalette();
  const formikProps = useFormik<{ paletteName: string }>({
    initialValues: { paletteName: '' },
    onSubmit: (values) =>
      createPaletteMutation({ name: values.paletteName }).then((id) =>
        reload().then(() => {
          setSelectedPalette(paletteList.find((p) => p.id === id));
          setPage('editPalette');
        })
      ),
  });

  // User
  const { result: user, query: userQuery, team } = usersApiService.queries.useGetCurrentUser();

  // Update my default palette
  const [defaultPaletteId, setDefaultPaletteId] = useState(user.defaultPaletteId ?? null);
  const handleUpdateUserPalette = usersApiService.mutations.useUpdateUserPalette();
  useEffect(() => setDefaultPaletteId(user?.defaultPaletteId ?? null), [user?.defaultPaletteId]);
  useEffect(() => {
    if (!user) return;
    if (defaultPaletteId === user.defaultPaletteId) return;
    handleUpdateUserPalette({
      paletteId: defaultPaletteId,
    }).then(() => userQuery.refetch());
  }, [defaultPaletteId]);

  // Palette delete
  const deletePaletteMutation =
    projectsApiService.mutations.projects.useDeleteAnalysisColorPalette();

  const [deleting, setDeleting] = useState(false);
  const handleDeletePalette = () => {
    setDeleting(true);
    deletePaletteMutation({ id: selectedPalette.id })
      .then(reload)
      .finally(() => setDeleting(false));
  };

  return (
    <VoxelPopover>
      <VoxelPopoverTrigger enableActive>
        <VoxelButton endIcon={<FontAwesomeIcon icon={faBrush} />} title="Style" size="md" />
      </VoxelPopoverTrigger>
      <VoxelPopoverContent paddingClass="p-6">
        {page === 'editPalette' && selectedPalette && (
          <PalettePage
            originalPalette={selectedPalette}
            goBack={() => setPage('start')}
            reload={reload}
          />
        )}
        {page === 'createPalette' && (
          <div className="w-80 flex flex-row gap-2 items-center">
            <div className="absolute top-4 left-4">
              <VoxelIconButton
                icon={<FontAwesomeIcon icon={faChevronLeft} />}
                color="white"
                onClick={() => setPage('start')}
                disabled={formikProps.isSubmitting}
              />
            </div>
            <div className="w-full mt-8 flex flex-row justify-between gap-2">
              <div className="grow">
                <VoxelInput
                  value={formikProps.values.paletteName}
                  onChange={(event) => formikProps.setFieldValue('paletteName', event.target.value)}
                  placeholder="New color palette..."
                  labelPosition="top"
                />
              </div>
              <VoxelButton
                title="Create"
                color="primary"
                disabled={!formikProps.values.paletteName.length || formikProps.isSubmitting}
                onClick={formikProps.submitForm}
                startIcon={<FontAwesomeIcon icon={faPlus} />}
              />
            </div>
          </div>
        )}

        {page === 'start' && (
          <div className="w-92 flex flex-col gap-6">
            <div className="flex flex-row gap-1 items-end">
              <div className="w-40">
                <VoxelSelectPalette
                  labelPosition="top"
                  selectedPalette={selectedPalette}
                  setSelectedPalette={(option) => setSelectedPalette(option)}
                />
              </div>
              <div className="w-1" />
              <VoxelButton
                startIcon={<FontAwesomeIcon icon={faPalette} />}
                title="Edit"
                onClick={() => setPage('editPalette')}
                color="transparent"
                disabled={
                  !selectedPalette ||
                  selectedPalette === null ||
                  deleting ||
                  !selectedPalette?.accessUserCanEdit
                }
              />
              <VoxelButton
                startIcon={<FontAwesomeIcon icon={faTrash} />}
                color="transparent"
                onClick={handleDeletePalette}
                title="Delete"
                disabled={!selectedPalette?.accessUserCanEdit || deleting}
              />
              <VoxelButton
                startIcon={<FontAwesomeIcon icon={faPlus} />}
                color="transparent"
                onClick={() => setPage('createPalette')}
                title="New"
                disabled={deleting || !user.accessUserCanCreatePalette}
              />

              <VoxelButton
                startIcon={<FontAwesomeIcon icon={faStar} />}
                color="transparent"
                onClick={() => setDefaultPaletteId(selectedPalette?.id ?? null)}
                title="Set as my default"
                disabled={deleting || selectedPalette?.id == user?.defaultPaletteId}
              />
            </div>
            <div className="w-40">
              <SlideThemeSelect
                analysisFilterState={analysisFilterState}
                setAnalysisFilterState={setAnalysisFilterState}
                disabled={!team?.specialAnalysisTheme}
                team={team}
              />
            </div>
          </div>
        )}
      </VoxelPopoverContent>
    </VoxelPopover>
  );
};

export default AnalysisStyleButton;
